import { Card, Button, Label, FormGroup, ButtonGroup, Row, Col, FormText } from 'reactstrap';
import { useEffect, useState, useContext } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import UploadDocument from './UploadDocuments';
import { TextInput, DollarInput } from '~/shared/components/form/Inputs';
import { FundRequestContext } from '~/context/fundrequests';
import { calculateTravelTotal } from '@shared/expenses/travel';
import { toUSD } from '~/shared/utils';

const TravelExpenses = ({ fieldArray, handleSubmission, travelSchema }) => {
    const {
        control,
        setValue,
        getValues,
        register,
        watch,
        formState: { errors, isSubmitting, isDirty, isValid },
    } = useFormContext();
    const [
        { expenseItem, expenseIndex, formKey, transactionsArray, annualRates },
        fundRequestDispatch,
    ] = useContext(FundRequestContext);
    const { remove } = fieldArray;
    const hasValidId = typeof expenseItem?.id === 'number';
    const [totalAmount, setTotalAmount] = useState(0);
    const [saving, setSaving] = useState(false);
    const existingUploads = watch(`${formKey}.existing_uploads`);
    const destination = watch(`${formKey}.destination`);

    // Watch numeric fields explicitly so totals recompute immediately on change
    const [
        miles_driven,
        parking_amount,
        toll_amount,
        car_rental,
        airfare,
        rideshare,
        repairs_maintenance,
    ] = watch([
        `${formKey}.miles_driven`,
        `${formKey}.parking_amount`,
        `${formKey}.toll_amount`,
        `${formKey}.car_rental`,
        `${formKey}.airfare`,
        `${formKey}.rideshare`,
        `${formKey}.repairs_maintenance`,
    ]);

    useEffect(() => {
        if (!formKey) return;
        const current = {
            miles_driven: parseFloat(miles_driven) || 0,
            parking_amount: parseFloat(parking_amount) || 0,
            toll_amount: parseFloat(toll_amount) || 0,
            car_rental: parseFloat(car_rental) || 0,
            airfare: parseFloat(airfare) || 0,
            rideshare: parseFloat(rideshare) || 0,
            repairs_maintenance: parseFloat(repairs_maintenance) || 0,
        };
        const total = calculateTravelTotal(current, annualRates, travelSchema);
        setTotalAmount(total || 0);
        setValue(`${formKey}.amount`, total || 0);
    }, [
        miles_driven,
        parking_amount,
        toll_amount,
        car_rental,
        airfare,
        rideshare,
        repairs_maintenance,
        annualRates,
        formKey,
        travelSchema,
        setValue,
    ]);

    const handleDeleteItem = () => {
        remove(expenseIndex);
        fundRequestDispatch({
            type: 'reset_expense_item',
        });
    };

    const handleCancelItem = () => {
        if (!expenseItem.id) {
            handleDeleteItem();
        } else {
            console.log('resetting expense item');
            fundRequestDispatch({
                type: 'reset_expense_item',
            });
        }
    };

    let hasErrors = false;
    if (transactionsArray in errors) {
        hasErrors = Object.keys(errors[transactionsArray]).length > 0;
        console.log('errors: ', errors[transactionsArray]);
    }

    const saveState = isDirty || existingUploads?.length;
    const saveText = saving || isSubmitting ? 'Saving...' : !saveState ? 'Done' : 'Save';

    return (
        <Card
            style={{
                paddingTop: 20,
                display: 'flex',
                backgroundColor: 'inherit',
            }}
        >
            {/* Dates in one row using Reactstrap grid */}
            <Row className='mb-3'>
                <Col xs={12} md={6}>
                    <TextInput
                        name={`${formKey}.date_of_travel`}
                        label='Date of Travel'
                        type='date'
                        control={control}
                        required={true}
                        aria-required={true}
                    />
                </Col>
                <Col xs={12} md={6}>
                    <TextInput
                        name={`${formKey}.date_of_travel_end`}
                        label='End Date (optional)'
                        type='date'
                        control={control}
                    />
                </Col>
            </Row>
            <Row className='mb-3'>
                <Col xs={12} md={6}>
                <TextInput
                    name={`${formKey}.destination`}
                    label='Destination'
                    control={control}
                    required={true}
                    aria-required={true}
                    maxLength={60}
                    newRules={{
                        maxLength: {
                            value: 60,
                            message:
                                'Destination must be 60 characters or less',
                        },
                        pattern: {
                            value: /^[^"]*$/,
                            message: 'Destination cannot contain double quote characters (")'
                        }
                    }}
                />
                <FormText className='text-muted'>
                    {(destination?.length || 0)}/60 characters
                </FormText>
                </Col>
                <Col xs={12} md={6}>
                <TextInput
                    name={`${formKey}.miles_driven`}
                    label='Miles Driven (Round Trip)'
                    type='number'
                    step='0.1'
                    min='0'
                    control={control}
                    helperText={`<strong>Note:</strong> Total Travel Cost reflects ${
                        annualRates?.year
                    } IRS
                medical mileage rate of ${toUSD(
                    annualRates?.medical_mileage
                )} per mile.`}
                />
                </Col>
            </Row>
            {/* Amount inputs in two columns using Reactstrap grid */}
            <Row className='mb-3'>
                <Col xs={12} md={6}>
                    <DollarInput
                        name={`${formKey}.parking_amount`}
                        label='Parking Amount'
                        control={control}
                        defaultValue={getValues(`${formKey}.parking_amount`) ?? ''}
                        setValue={setValue}
                    />
                </Col>
                <Col xs={12} md={6}>
                    <DollarInput
                        name={`${formKey}.toll_amount`}
                        label='Toll Amount'
                        control={control}
                        defaultValue={getValues(`${formKey}.toll_amount`) ?? ''}
                        setValue={setValue}
                    />
                </Col>
                <Col xs={12} md={6}>
                    <DollarInput
                        name={`${formKey}.car_rental`}
                        label='Car Rental'
                        control={control}
                        defaultValue={getValues(`${formKey}.car_rental`) ?? ''}
                        setValue={setValue}
                    />
                </Col>
                <Col xs={12} md={6}>
                    <DollarInput
                        name={`${formKey}.airfare`}
                        label='Airfare'
                        control={control}
                        defaultValue={getValues(`${formKey}.airfare`) ?? ''}
                        setValue={setValue}
                    />
                </Col>
                <Col xs={12} md={6}>
                    <DollarInput
                        name={`${formKey}.rideshare`}
                        label='Rideshare (Uber, Lyft, etc.)'
                        control={control}
                        defaultValue={getValues(`${formKey}.rideshare`) ?? ''}
                        setValue={setValue}
                    />
                </Col>
                <Col xs={12} md={6}>
                    <DollarInput
                        name={`${formKey}.repairs_maintenance`}
                        label='Repairs/Maintenance'
                        control={control}
                        defaultValue={getValues(`${formKey}.repairs_maintenance`) ?? ''}
                        setValue={setValue}
                    />
                </Col>
            </Row>
            <Label>Total Travel Cost</Label>
            <strong>{toUSD(totalAmount)}</strong>
            {hasValidId && <UploadDocument />}
            <ButtonGroup className='d-grid d-sm-flex gap-2 mt-4'>
                <Button
                    color='outline-dark'
                    onClick={handleCancelItem}
                    disabled={isSubmitting}
                >
                    Cancel
                </Button>
                {hasValidId && (
                    <>
                        <Button
                            color='danger'
                            onClick={handleDeleteItem}
                            disabled={isSubmitting || saving}
                        >
                            Delete
                        </Button>
                        <Button
                            color='primary'
                            onClick={async () => {
                                // If nothing to save, close the form (Done)
                                if (!saveState) {
                                    fundRequestDispatch({ type: 'reset_expense_item' });
                                    return;
                                }
                                try {
                                    setSaving(true);
                                    await handleSubmission({ permanentSave: false });
                                } finally {
                                    setSaving(false);
                                }
                            }}
                            disabled={
                                hasErrors ||
                                isSubmitting || saving ||
                                !isValid ||
                                totalAmount === 0
                            }
                        >
                            {saveText}
                        </Button>
                    </>
                )}
                {!hasValidId && (
                    <Button
                        color='primary'
                        onClick={async () => {
                            // If nothing to save, close the form (Done)
                            if (!saveState) {
                                fundRequestDispatch({ type: 'reset_expense_item' });
                                return;
                            }
                            try {
                                setSaving(true);
                                const updatedItem = await handleSubmission({ permanentSave: false });
                                if (updatedItem && typeof updatedItem.id === 'number') {
                                    fundRequestDispatch({
                                        type: 'set_expense_item',
                                        expenseItem: updatedItem,
                                        expenseType: 'travel',
                                    });
                                }
                            } finally {
                                setSaving(false);
                            }
                        }}
                        disabled={
                            hasErrors ||
                            isSubmitting || saving ||
                            !isValid ||
                            totalAmount === 0
                        }
                    >
                        {saving || isSubmitting ? `Saving...` : `Next: Upload Documents`}
                    </Button>
                )}
            </ButtonGroup>
        </Card>
    );
};

export default TravelExpenses;
